// Moving and resizing a crop by hand, in pixels of the whole photo. Free, it keeps any shape; held to a shape (a
// width over height, 1 for a square), it keeps that shape and is resized from its corners only.
import { MIN_CROP, type Crop } from '../../shared/crop'

export type Handle = 'move' | 'n' | 's' | 'e' | 'w' | 'nw' | 'ne' | 'sw' | 'se'
type Size = { width: number; height: number }

const clamp = (n: number, low: number, high: number) => Math.min(Math.max(n, low), high)

// The crop `start` dragged by (dx, dy) pixels of the photo, by one of its handles, kept inside the photo.
export function dragCrop(start: Crop, handle: Handle, dx: number, dy: number, size: Size, ratio: number | null): Crop {
  if (handle === 'move') {
    return {
      ...start,
      x: Math.round(clamp(start.x + dx, 0, size.width - start.width)),
      y: Math.round(clamp(start.y + dy, 0, size.height - start.height)),
    }
  }
  const min = Math.min(MIN_CROP, size.width, size.height)
  const west = handle.includes('w')
  const north = handle.includes('n')
  if (ratio) {
    // The corner opposite the one held stays put; the size follows whichever way the finger went further.
    if (handle.length !== 2) return start
    const ax = west ? start.x + start.width : start.x
    const ay = north ? start.y + start.height : start.y
    const wide = start.width + (west ? -dx : dx)
    const tall = (start.height + (north ? -dy : dy)) * ratio
    const room = Math.min(west ? ax : size.width - ax, (north ? ay : size.height - ay) * ratio)
    const width = Math.floor(clamp(Math.max(wide, tall), Math.max(min, min * ratio), room))
    const height = Math.min(Math.round(width / ratio), north ? ay : size.height - ay)
    return { ...start, x: west ? ax - width : ax, y: north ? ay - height : ay, width, height }
  }
  let left = start.x
  let top = start.y
  let right = start.x + start.width
  let bottom = start.y + start.height
  if (west) left = clamp(left + dx, 0, right - min)
  if (handle.includes('e')) right = clamp(right + dx, left + min, size.width)
  if (north) top = clamp(top + dy, 0, bottom - min)
  if (handle.includes('s')) bottom = clamp(bottom + dy, top + min, size.height)
  const x = Math.round(left)
  const y = Math.round(top)
  return { ...start, x, y, width: Math.round(right) - x, height: Math.round(bottom) - y }
}
